import Analytics from '@opencare/analytics';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { COUNTRY } from 'common/constants';
import { ValidationRulePreset } from 'common/Validator/ValidationRules';
import { createImmutableFormValidatorConfig, FieldValidation, useFormValidator } from 'common/Validator/Validator';
import { UpdatePatientDetails } from 'connect/saga/handlers/patient/update-details';
import { selectDataClinicCountry } from 'connect/selectors';
import { Checkbox as FormCheckbox, FormCheckboxWrapper } from 'pages/checkout/components/common/checkbox-styled-component';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { Form, StepFormWrapper, StepHeader, StepHeaderSubtext, StepHeaderText, StepWrapper } from 'pages/checkout/components/common/layout-styled-components';
import { SmsPromoDisclaimer } from 'pages/checkout/components/common/SmsPromoDisclaimer';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { Bolded, PhoneNumberSubmitButton } from 'pages/checkout/steps/PhoneNumber/styled-components';
import { formatPhoneNumberForApi } from 'pages/checkout/utils/phoneUtils';
import { useAnalyticsService } from 'services/AnalyticsService';

const analytics = new Analytics();

const PHONE_NUMBER_FORM_VALIDATOR_CONFIG = createImmutableFormValidatorConfig<void>([
  { field: 'phone', preset: ValidationRulePreset.phone },
  { field: 'phone', preset: ValidationRulePreset.phoneCountryCode },
]);

interface ValidationState {
  allValid?: boolean;
  phone?: FieldValidation;
  phoneCountryCode?: FieldValidation;
}

export const PhoneNumber = () => {
  const dispatch = useDispatch();
  const { goToNextStep } = useNextCheckoutStep();
  const analyticsService = useAnalyticsService();
  const formValidator = useFormValidator(PHONE_NUMBER_FORM_VALIDATOR_CONFIG);

  const clinicCountry = useSelector(selectDataClinicCountry);

  const isLoading = useSelector(UpdatePatientDetails.loading!.selector);
  const error = useSelector(UpdatePatientDetails.error!.selector);
  const success = useSelector(UpdatePatientDetails.success!.selector);

  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [phone, setPhone] = useState<string>('');
  const [promoSmsAllowed, setPromoSmsAllowed] = useState(false);
  const [formValidity, setFormValidity] = useState<ValidationState>({});

  const validationState = useMemo(() => ({ phone }), [phone]);

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-phone-number',
      name: 'checkout',
      section: 'phone-number',
      ...analyticsService.analyticsData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every time, so we don't want it in the dependency array
  }, []);

  const handleValidate = useCallback(() => {
    const validationResult = formValidator.validate(validationState);
    setFormValidity(validationResult);
    return validationResult;
  }, [validationState, formValidator]);

  // Validate when inputs change
  useEffect(() => {
    if (!validationState) {
      return;
    }

    handleValidate();
  }, [handleValidate, validationState]);

  useEffect(() => {
    if (success && !isLoading) {
      dispatch(UpdatePatientDetails.success!.update(null));

      analytics.trackForm({
        pageType: analytics.pageTypes.CHECKOUT,
        pageName: 'checkout-phone-number',
        name: 'checkout',
        section: 'phone-number',
        ...analyticsService.analyticsData,
        content: {
          phone_provided: String(!!phone),
          promo_sms_allowed: String(promoSmsAllowed),
        },
      });

      // Use replace because this step will become inapplicable
      // As soon as the phone number is provided.
      // If we don't use replace, the user can't go back using the browser's back button.
      goToNextStep({ replace: true });
    }
  }, [success, isLoading, dispatch, goToNextStep, phone, promoSmsAllowed]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationResult = handleValidate();

    if (!validationResult.allValid) {
      setShowValidationErrors(true);
      return;
    }

    dispatch(UpdatePatientDetails.createAction({
      patientDetails: {
        mobilePhone: formatPhoneNumberForApi(phone),
        settings: {
          promoSmsAllowed,
        },
      },
      autoVerify: true,
    }));
  };

  const handleSmsPromoToggle = () => {
    const newPromoSmsAllowed = !promoSmsAllowed;
    setPromoSmsAllowed(newPromoSmsAllowed);

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-phone-number',
      name: 'checkout',
      section: 'phone-number',
      content: {
        action: 'sms-promo-toggle',
        sms_promo_allowed: String(newPromoSmsAllowed),
      },
    });
  };

  const phoneErrorMessage = useMemo(() => {
    if (!showValidationErrors) {
      return undefined;
    }

    const { phone, phoneCountryCode } = formValidity;

    return phone?.message || phoneCountryCode?.message;
  }, [showValidationErrors, formValidity]);

  return (
    <StepWrapper>
      <StepHeader>
        <StepHeaderText>Enter Phone Number</StepHeaderText>
        <StepHeaderSubtext>
          We'll use this number to verify your booking and keep you updated about your appointment.
        </StepHeaderSubtext>
      </StepHeader>

      <StepFormWrapper>
        {error && <FormError>{error}</FormError>}
        <Form onSubmit={handleSubmit}>
          <Input
            type={InputType.TEL}
            placeholder="Enter your phone"
            autoComplete="tel-national"
            name="phone"
            value={phone}
            onChange={handlePhoneChange}
            errorMessage={phoneErrorMessage}
          />
          {clinicCountry === COUNTRY.unitedStates
            && (
              <FormCheckboxWrapper>
                <FormCheckbox
                  checked={promoSmsAllowed}
                  onChange={handleSmsPromoToggle}
                />
                <span>
                  Yes, I'd also like to receive occasional promotional texts about offers from Opencare.
                  Message and data rates may apply.
                  You can unsubscribe anytime by texting <Bolded>STOP</Bolded>.
                </span>
              </FormCheckboxWrapper>
            )}

          <PhoneNumberSubmitButton
            type="submit"
            $disabled={!formValidity.allValid}
            $loading={isLoading}
          >
            Continue Booking
          </PhoneNumberSubmitButton>
        </Form>
      </StepFormWrapper>

      <SmsPromoDisclaimer includeMsgFrequencyCopy={false} />
    </StepWrapper>
  );
};
